import { existsSync, readFileSync, writeFileSync } from "fs"
import { join } from "path"
import type { FeatureInstaller, ProjectConfig } from "../../types/types.js"
import { logger } from "../../utils/logger.js"
import { PackageManagerAdapter } from "../../utils/package-manager.js"

export class ESLintPrettierInstaller implements FeatureInstaller {
  name = "ESLint + Prettier"

  async install(config: ProjectConfig): Promise<void> {
    logger.info("Setting up ESLint + Prettier...")
    const pm = new PackageManagerAdapter(config.packageManager)
    const deps = this.getDependencies(config)
    await pm.add(deps, true, config)
    this.createESLintConfig(config)
    this.createPrettierConfig(config)
    this.createIgnoreFiles(config)
    this.updateScripts(config)
    logger.success("ESLint + Prettier configured successfully")
  }

  private getDependencies(config: ProjectConfig): Array<string> {
    const base = [
      "eslint",
      "prettier",
      "eslint-config-prettier",
      "eslint-plugin-prettier",
    ]

    if (config.typescript) {
      base.push("@typescript-eslint/parser", "@typescript-eslint/eslint-plugin")
    }

    switch (config.framework) {
      case "react":
        base.push("eslint-plugin-react", "eslint-plugin-react-hooks")
        if (config.metaFramework === "next") {
          base.push("eslint-config-next")
        }
        break
      case "vue":
        base.push("eslint-plugin-vue")
        break
      case "svelte":
        base.push("eslint-plugin-svelte")
        break
    }
    return base
  }

  private createESLintConfig(config: ProjectConfig): void {
    const eslintConfig: Record<string, unknown> = {
      env: {
        browser: true,
        es2022: true,
        node: true,
      },
      extends: this.getESLintExtends(config),
      parser: config.typescript ? "@typescript-eslint/parser" : undefined,
      parserOptions: {
        ecmaVersion: "latest",
        sourceType: "module",
        ...(config.framework === "react" && {
          ecmaFeatures: {
            jsx: true,
          },
        }),
      },
      plugins: this.getESLintPlugins(config),
      root: true,
      rules: {
        "prettier/prettier": "error",
        ...this.getFrameworkRules(config),
      },
      settings: this.getESLintSettings(config),
    }

    for (const key of Object.keys(eslintConfig)) {
      if (eslintConfig[key] === undefined) {
        delete eslintConfig[key]
      }
    }

    const configPath = join(config.projectPath, ".eslintrc.json")
    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create ESLint config")
      return
    }

    writeFileSync(configPath, JSON.stringify(eslintConfig, null, 2))
    logger.debug("Created .eslintrc.json")
  }

  private getESLintExtends(config: ProjectConfig): Array<string> {
    const extendsList: Array<string> = ["eslint:recommended"]

    if (config.typescript) {
      extendsList.push("plugin:@typescript-eslint/recommended")
    }

    switch (config.framework) {
      case "react":
        extendsList.push("plugin:react/recommended")
        extendsList.push("plugin:react-hooks/recommended")
        if (config.metaFramework === "next") {
          extendsList.push("next/core-web-vitals")
        }
        break
      case "vue":
        extendsList.push("plugin:vue/vue3-recommended")
        break
      case "svelte":
        extendsList.push("plugin:svelte/recommended")
        break
    }

    extendsList.push("plugin:prettier/recommended")
    return extendsList
  }

  private getESLintPlugins(config: ProjectConfig): Array<string> {
    const plugins: Array<string> = []

    if (config.typescript) {
      plugins.push("@typescript-eslint")
    }

    switch (config.framework) {
      case "react":
        plugins.push("react", "react-hooks")
        break
      case "vue":
        plugins.push("vue")
        break
      case "svelte":
        plugins.push("svelte")
        break
    }

    return plugins
  }

  private getFrameworkRules(config: ProjectConfig): Record<string, string> {
    const rules: Record<string, string> = {}

    if (config.framework === "react") {
      rules["react/react-in-jsx-scope"] = "off"
      rules["react/prop-types"] = "off"
    }

    if (config.typescript) {
      rules["@typescript-eslint/explicit-module-boundary-types"] = "off"
      rules["@typescript-eslint/no-explicit-any"] = "warn"
    }

    return rules
  }

  private getESLintSettings(
    config: ProjectConfig,
  ): Record<string, unknown> | undefined {
    if (config.framework === "react") {
      return {
        react: {
          version: "detect",
        },
      }
    }
    return undefined
  }

  private createPrettierConfig(config: ProjectConfig): void {
    const prettierConfig = {
      arrowParens: "always",
      bracketSameLine: false,
      bracketSpacing: true,
      embeddedLanguageFormatting: "auto",
      endOfLine: "lf",
      htmlWhitespaceSensitivity: "css",
      jsxSingleQuote: false,
      printWidth: 100,
      proseWrap: "preserve",
      semi: true,
      singleQuote: true,
      tabWidth: 2,
      trailingComma: "es5",
      useTabs: false,
    }

    const configPath = join(config.projectPath, ".prettierrc")
    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create Prettier config")
      return
    }

    writeFileSync(configPath, JSON.stringify(prettierConfig, null, 2))
    logger.debug("Created .prettierrc")
  }

  private createIgnoreFiles(config: ProjectConfig): void {
    const ignorePatterns = [
      "node_modules",
      "dist",
      "build",
      ".next",
      ".nuxt",
      ".svelte-kit",
      ".astro",
      "coverage",
      "*.min.js",
      "*.min.css",
      "package-lock.json",
      "pnpm-lock.yaml",
      "yarn.lock",
      "bun.lockb",
    ]

    const eslintIgnorePath = join(config.projectPath, ".eslintignore")
    const prettierIgnorePath = join(config.projectPath, ".prettierignore")

    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create ignore files")
      return
    }

    const ignoreContent = ignorePatterns.join("\n")
    writeFileSync(eslintIgnorePath, ignoreContent)
    writeFileSync(prettierIgnorePath, ignoreContent)
    logger.debug("Created .eslintignore and .prettierignore")
  }

  private updateScripts(config: ProjectConfig): void {
    if (config.dryRun) {
      logger.debug("[DRY RUN] Would update package.json scripts")
      return
    }

    const packageJsonPath = join(config.projectPath, "package.json")

    if (!existsSync(packageJsonPath)) {
      logger.warning("package.json not found, skipping script updates")
      return
    }

    const packageJson = JSON.parse(readFileSync(packageJsonPath, "utf-8"))

    packageJson.scripts = {
      ...packageJson.scripts,
      format: "prettier --write .",
      "format:check": "prettier --check .",
      lint: "eslint . --ext .js,.jsx,.ts,.tsx",
      "lint:fix": "eslint . --ext .js,.jsx,.ts,.tsx --fix",
    }

    writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2))
    logger.debug("Updated package.json scripts")
  }
}
